// Shared GLTFLoader factory — wires Draco (geometry) + KTX2/Basis (texture)
// decoders into every loader instance. Content's shells (hub/nocturn) + comic
// GLBs are Draco-compressed + KTX2-textured (the M7 bloat fix), so ANY loader
// that fetches them needs both decoders — otherwise GLTFLoader throws
// "No DRACOLoader instance provided". stage.js has its own equivalent inline
// setup (the main runtime loader, created before its renderer exists); this
// factory covers the auxiliary loaders (studio viewport + thumbnail renderer,
// authoring scene-view). TODO: fold stage.js onto this factory to de-dup.
//
// The DRACOLoader + KTX2Loader are module-level SINGLETONS reused across every
// GLTFLoader — decoders are stateless workers and KTX2 detectSupport() probes
// machine-wide GPU caps, so detecting once (first renderer wins) is correct.
//
// The SAME KTX2 singleton also serves LOOSE `.ktx2` maps (DECISION-053 Slice-2, Step 1) —
// material-depth loads standalone baseColor/normal/roughness/… maps (not GLB-embedded), and
// they must transcode through the same GPU-detected transcoder. `loadKtx2Map` is that path.

import * as THREE from "../../vendor/three.module.js";
import { GLTFLoader } from "../../vendor/GLTFLoader.js";
import { DRACOLoader } from "../../vendor/DRACOLoader.js";
import { KTX2Loader } from "../../vendor/KTX2Loader.js";
import { resolveKtx2Config, ktx2CacheKey } from "./_ktx2-texture-config.js";
import { declaresMeshopt } from "./_meshopt-declared.js";

// EXACT `import.meta.env.BASE_URL` (no optional chaining) so Vite static-replaces
// it; mirrors _asset-url-resolver.js + stage.js. Decoder binaries live under
// runtime/public/vendor/{draco/gltf,basis} → served at the bundle root.
function assetBase() {
	try {
		const b = import.meta.env.BASE_URL;
		if (typeof b === "string" && b.length > 0) return b;
	} catch { /* not under Vite (CLI test) — fall through */ }
	return "./";
}

let _draco = null;
let _ktx2 = null;
let _ktx2Detected = false;
const _ktx2MapCache = new Map();   // ktx2CacheKey → Promise<THREE.Texture> (dedup in-flight + done)
const WRAP = { repeat: THREE.RepeatWrapping, clamp: THREE.ClampToEdgeWrapping };

/**
 * The shared KTX2/Basis transcoder singleton. Ensures GPU support is probed exactly once
 * (first renderer wins). Both the GLTFLoader (embedded textures) and `loadKtx2Map` (loose
 * maps) go through this instance so a single detectSupport() covers both.
 * @param {THREE.WebGLRenderer} [renderer] — pass when available so KTX2 transcodes.
 * @returns {KTX2Loader}
 */
export function getKtx2Loader(renderer) {
	if (!_ktx2) _ktx2 = new KTX2Loader().setTranscoderPath(`${assetBase()}vendor/basis/`);
	if (renderer && !_ktx2Detected) { _ktx2.detectSupport(renderer); _ktx2Detected = true; }
	return _ktx2;
}

// ─── meshopt (EXT_meshopt_compression) — LAZY, on purpose ─────────────────────
// Wired 2026-08-27 (ASK-124 B1-G) because the GG export path emits this extension
// alongside `KHR_texture_basisu` (gltfpack `-cc`), and GLTFLoader THROWS on it —
// "setMeshoptDecoder must be called before loading compressed files" — rather than
// degrading. A loader without it does not read the platform's own declared container.
//
// LAZY rather than a static import, and the reason is measured, not stylistic:
// a static import puts the decoder in the customer's INITIAL bundle and takes it
// from 1359.2 KB to 1388.2 KB, past the 1380 KB ceiling `check-bundle-budget.mjs`
// enforces (+29.0 KB, and it lands in the vendor chunk). Meanwhile ZERO of the 58
// live catalogue GLBs use the extension today — it is meshsmith's forward export
// path. So the decoder is fetched on the first GLB that actually declares it, and
// never on a customer who is only served the current corpus.
//
// The race that would otherwise open — a meshopt GLB arriving before the decoder
// lands — is closed in `parse` below: a payload that DECLARES meshopt waits for
// the decoder; one that does not is untouched and pays nothing.
let _meshopt = null;
let _meshoptPromise = null;
const _loaders = new Set();

/**
 * Fetch + initialise the meshopt decoder and attach it to every loader this factory
 * has made (and will make). Idempotent; safe to call from anywhere.
 * @returns {Promise<object>} the decoder
 */
export function ensureMeshoptDecoder() {
	if (!_meshoptPromise) {
		_meshoptPromise = import("../../vendor/meshopt_decoder.module.js")
			.then(async ({ MeshoptDecoder }) => {
				await MeshoptDecoder.ready;
				_meshopt = MeshoptDecoder;
				for (const l of _loaders) l.setMeshoptDecoder(_meshopt);
				return _meshopt;
			})
			.catch((err) => { _meshoptPromise = null; throw err; });   // don't cache a failure
	}
	return _meshoptPromise;
}

/**
 * Create a GLTFLoader configured with the shared Draco + KTX2 decoders, and with
 * meshopt resolved on demand (see the block above).
 *
 * @param {THREE.WebGLRenderer} [renderer] — needed once for KTX2 detectSupport
 *   (GPU-format probe). Safe to omit on secondary loaders once any loader has
 *   detected; pass it when available so KTX2 textures transcode.
 * @returns {GLTFLoader}
 */
export function createGltfLoader(renderer) {
	if (!_draco) _draco = new DRACOLoader().setDecoderPath(`${assetBase()}vendor/draco/gltf/`);
	const loader = new GLTFLoader().setDRACOLoader(_draco).setKTX2Loader(getKtx2Loader(renderer));
	if (_meshopt) loader.setMeshoptDecoder(_meshopt);
	_loaders.add(loader);

	const parse = loader.parse.bind(loader);
	loader.parse = (data, path, onLoad, onError) => {
		if (_meshopt || !declaresMeshopt(data)) return parse(data, path, onLoad, onError);
		ensureMeshoptDecoder()
			.then(() => parse(data, path, onLoad, onError))
			.catch((err) => (onError ? onError(err) : console.error("[gltf] meshopt decoder failed to load", err)));
	};
	return loader;
}

/**
 * Load a LOOSE `.ktx2` map (standalone baseColor/normal/roughness/… — NOT GLB-embedded) as a
 * CompressedTexture configured for its PBR role (DECISION-053 Slice-2). Transcodes through the
 * shared, GPU-detected KTX2 singleton — so ensure a renderer has detected support first
 * (pass one here, or via a prior createGltfLoader/getKtx2Loader). Deduped + cached by
 * url + colour-class (see ktx2CacheKey). A failed load is evicted so a later attempt retries.
 * @param {string} url — the `.ktx2` URL (already CDN-resolved by the caller).
 * @param {string} role — PBR role: baseColor|normal|roughness|metalness|ao|emissive|…
 * @param {{ renderer?: THREE.WebGLRenderer, anisotropy?: number, wrap?: "repeat"|"clamp" }} [opts]
 * @returns {Promise<THREE.Texture>}
 */
export function loadKtx2Map(url, role, { renderer, anisotropy = 8, wrap = "repeat" } = {}) {
	const key = ktx2CacheKey(url, role);
	const hit = _ktx2MapCache.get(key);
	if (hit) return hit;
	const loader = getKtx2Loader(renderer);
	const cfg = resolveKtx2Config(role, { anisotropy, wrap });
	const p = loader.loadAsync(url).then((tex) => {
		tex.colorSpace = cfg.colorSpace;
		tex.flipY = cfg.flipY;
		tex.wrapS = tex.wrapT = WRAP[cfg.wrap] ?? THREE.RepeatWrapping;
		tex.anisotropy = cfg.anisotropy;
		tex.needsUpdate = true;
		return tex;
	}).catch((err) => {
		_ktx2MapCache.delete(key);   // don't cache a failure — allow retry
		throw err;
	});
	_ktx2MapCache.set(key, p);
	return p;
}

/** Test/hot-reload seam — drop cached loose-map promises. */
export function _resetKtx2MapCache() { _ktx2MapCache.clear(); }

/**
 * ASK-142 R2c — a GLTFLoader whose PARSE yields to the frame every `budgetMs` of work (Unreal's
 * async-loading time limit / Unity's background-loading priority: an asset that arrives mid-session
 * is built in slices, never in one stall). GLTFParser resolves its whole dependency graph inside one
 * task — a skinned, animated companion measured 40–56 ms in a walking frame. The plugin wraps the
 * parser's per-dependency loaders so each one, before it runs, gives the event loop a turn once the
 * slice is spent; the result is identical, it only arrives over several frames.
 * @param {THREE.WebGLRenderer} [renderer]
 * @param {number} [budgetMs]
 */
export function createTimeSlicedGltfLoader(renderer, budgetMs = 6) {
	const loader = createGltfLoader(renderer);
	let sliceStart = performance.now();
	const yieldIfSpent = async () => {
		if (performance.now() - sliceStart < budgetMs) return;
		await new Promise((r) => setTimeout(r, 0));
		sliceStart = performance.now();
	};
	loader.register((parser) => {
		for (const fn of ["loadAccessor", "loadAnimation", "loadMesh", "loadNode", "loadSkin", "loadMaterial", "loadTexture"]) {
			const orig = parser[fn]?.bind(parser);
			if (!orig) continue;
			parser[fn] = async (...args) => { await yieldIfSpent(); return orig(...args); };
		}
		return { name: "KAPOWX_time_sliced_parse" };
	});
	return loader;
}
